// src/pages/AboutPage.tsx
import { Link } from 'react-router-dom'
import SEOHead from '../components/SEOHead'
import ScrollReveal from '../components/ScrollReveal'
import styles from './AboutPage.module.css'

const VALUES = [
  { icon: '🎯', title: 'Precision Placement', desc: 'We never place a machine without understanding the venue and its customers first.' },
  { icon: '🤝', title: 'Genuine Partnership', desc: "We're not vendors — we're partners. When your location succeeds, we succeed." },
  { icon: '📍', title: 'Edmonton-First', desc: "We're committed to YEG. Local businesses, local jobs, local reinvestment — always." },
  { icon: '⚡', title: 'Convenience That Counts', desc: 'We place machines that customers actively choose — because it\'s exactly what they need.' },
  { icon: '💰', title: 'Zero-Risk Revenue', desc: 'No investment, no liability, just passive income for Edmonton businesses from day one.' },
  { icon: '📈', title: 'Business Growth', desc: 'Vending done right adds to your bottom line, improves experience, and makes you more valuable.' },
]

const TIMELINE = [
  { year: '2022', color: 'var(--blue)', title: 'The Idea Sparks in Edmonton', desc: 'Noticed a glaring gap in YEG\'s vending market — the wrong machines in the wrong venues. Started researching what Edmonton\'s best businesses actually needed.' },
  { year: '2023', color: 'var(--green)', title: 'First Gym Partnership in YEG', desc: 'Signed our first Edmonton gym partnership and installed a protein vending machine. Member response was immediate — the demand was real.' },
  { year: '2024', color: 'var(--blue)', title: 'Expanding to Bars & Offices', desc: 'Launched vape vending with Edmonton bars and snack vending with local offices. Refined our model and built our service team.' },
  { year: '2025', color: 'var(--green)', title: 'Growing Across Edmonton & Alberta', desc: 'Multiple active placements across YEG and surrounding communities. Building toward becoming the undisputed vending leader in Alberta.' },
  { year: 'Now', color: 'gradient', title: 'The Best Is Still Ahead', desc: "Actively expanding our Edmonton portfolio and taking on new venue partners. If you have a location, now is the perfect time to get on board." },
]

const COMMUNITY = [
  { icon: '🏙️', title: 'Local Business First', desc: 'We prioritize partnerships with locally-owned Edmonton venues over national chains.' },
  { icon: '💼', title: 'Edmonton Jobs', desc: 'Our team is made up entirely of Edmonton residents. Every dollar gets reinvested locally.' },
  { icon: '🌱', title: 'Supporting Growth', desc: "We believe Edmonton's business community is only as strong as its weakest player." },
  { icon: '🤝', title: 'Long-Term Relationships', desc: "We're not in this for a quick placement — we build lasting partnerships built on trust." },
]

const ABOUT_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  url: 'https://www.freshdispense.com/about',
  name: 'About FreshDispense — Edmonton Vending Machine Company',
  mainEntity: {
    '@type': 'LocalBusiness',
    name: 'FreshDispense',
    foundingLocation: { '@type': 'City', name: 'Edmonton' },
    description: "Edmonton's premier vending machine placement company serving YEG and Alberta.",
  },
}

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title="About FreshDispense | Edmonton Vending Company | Our Story & Mission | YEG"
        description="FreshDispense is Edmonton's homegrown vending machine placement company. Founded in YEG, we help Alberta businesses grow with custom vending solutions. Learn our story."
        canonical="/about"
        keywords="about FreshDispense, Edmonton vending company, YEG vending business, local Edmonton vending, Alberta vending company"
        schema={ABOUT_SCHEMA}
      />

      <main className={`page-enter ${styles.main}`}>
        {/* ─── PAGE HERO ─── */}
        <section className={styles.pageHero} aria-label="About page header">
          <div className="container">
            <nav className={styles.breadcrumb} aria-label="Breadcrumb">
              <Link to="/">Home</Link><span className={styles.breadSep}>/</span><span>About Us</span>
            </nav>
            <ScrollReveal>
              <span className="badge badge-green"><span className="badge-dot" />Edmonton's Own</span>
            </ScrollReveal>
            <ScrollReveal delay={100}>
              <h1 className={styles.pageH1}>
                Born in YEG.<br />
                <span className="gradient-text">Built to Help Edmonton</span><br />
                Businesses Grow.
              </h1>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <p className={styles.pageSubtitle}>
                FreshDispense is more than a vending company — we're Edmonton locals who believe
                every business deserves a passive revenue stream that works as hard as they do.
              </p>
            </ScrollReveal>
          </div>
        </section>

        {/* ─── VALUES ─── */}
        <section className={styles.valuesSection} aria-labelledby="values-heading">
          <div className="container">
            <ScrollReveal><span className="label-tag">What We Stand For</span></ScrollReveal>
            <ScrollReveal delay={100}>
              <h2 id="values-heading" style={{ fontSize: 'clamp(28px,4vw,52px)', marginBottom: '0.75rem' }}>
                Our Mission &amp; Values
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <p style={{ maxWidth: 560, fontSize: 17, marginBottom: '3rem' }}>
                Every decision at FreshDispense comes back to three things: the right product,
                the right place, and real benefit for Edmonton businesses.
              </p>
            </ScrollReveal>
            <div className={styles.valuesGrid}>
              {VALUES.map((v, i) => (
                <ScrollReveal key={v.title} delay={(i % 3) * 100}>
                  <div className={styles.valueCard}>
                    <div style={{ fontSize: 44, marginBottom: '1.2rem' }}>{v.icon}</div>
                    <h3 style={{ fontSize: 20, color: 'var(--text)', marginBottom: '0.75rem' }}>{v.title}</h3>
                    <p style={{ fontSize: 14, lineHeight: 1.7 }}>{v.desc}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* ─── TIMELINE ─── */}
        <section className="section" aria-labelledby="timeline-heading">
          <div className="container">
            <ScrollReveal><span className="label-tag">Our Journey</span></ScrollReveal>
            <ScrollReveal delay={100}>
              <h2 id="timeline-heading" style={{ fontSize: 'clamp(28px,4vw,52px)', marginBottom: '3rem' }}>
                From Edmonton Idea to<br />Alberta's Vending Specialists
              </h2>
            </ScrollReveal>
            <div className={styles.timeline}>
              {TIMELINE.map((item, i) => (
                <ScrollReveal key={item.year} delay={i * 80}>
                  <div className={styles.timelineItem}>
                    <div className={styles.timelineYear} style={{ color: item.color === 'gradient' ? 'var(--blue-light)' : item.color }}>
                      {item.year}
                    </div>
                    <div className={styles.timelineCenter}>
                      <div
                        className={styles.timelineDot}
                        style={{
                          background: item.color === 'gradient'
                            ? 'linear-gradient(135deg,var(--blue),var(--green))'
                            : item.color,
                          borderColor: item.color === 'gradient' ? 'transparent' : item.color,
                        }}
                      />
                      {i < TIMELINE.length - 1 && (
                        <div
                          className={styles.timelineLine}
                          style={{ background: `linear-gradient(${item.color === 'gradient' ? 'var(--blue-light)' : item.color},rgba(63,168,232,0.1))` }}
                        />
                      )}
                    </div>
                    <div className={styles.timelineContent}>
                      <h4 style={{ fontSize: 18, color: 'var(--text)', marginBottom: '0.4rem' }}>{item.title}</h4>
                      <p style={{ fontSize: 14, lineHeight: 1.7 }}>{item.desc}</p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* ─── COMMUNITY ─── */}
        <section className={styles.communitySection} aria-labelledby="community-heading">
          <div className="container">
            <ScrollReveal><span className="label-tag">Edmonton Proud</span></ScrollReveal>
            <ScrollReveal delay={100}>
              <h2 id="community-heading" style={{ fontSize: 'clamp(28px,4vw,52px)', marginBottom: '0.75rem' }}>
                Investing in the<br />Edmonton Community
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <p style={{ maxWidth: 560, fontSize: 17, marginBottom: '2.5rem' }}>
                Being a YEG company means more than just having an Edmonton address.
              </p>
            </ScrollReveal>
            <div className={styles.communityGrid}>
              {COMMUNITY.map((c, i) => (
                <ScrollReveal key={c.title} delay={(i % 2) * 100}>
                  <div className={styles.communityCard}>
                    <div style={{ fontSize: 28, flexShrink: 0 }}>{c.icon}</div>
                    <div>
                      <h4 style={{ fontSize: 15, color: 'var(--text)', marginBottom: '0.3rem' }}>{c.title}</h4>
                      <p style={{ fontSize: 13 }}>{c.desc}</p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* ─── CTA ─── */}
        <section className="section" aria-label="CTA">
          <div className="container">
            <ScrollReveal>
              <div className="cta-banner">
                <h2 style={{ fontSize: 'clamp(28px,4vw,52px)', marginBottom: '1rem' }}>
                  Want to Partner with<br />FreshDispense in Edmonton?
                </h2>
                <p style={{ fontSize: 18, maxWidth: 520, margin: '0 auto 2.5rem' }}>
                  Get in touch and let's talk about how a custom vending placement can help your Edmonton business grow.
                </p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Link to="/contact" className="btn btn-primary">Start the Conversation →</Link>
                  <Link to="/services" className="btn btn-outline">View Our Services</Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>
      </main>
    </>
  )
}
